import { Coins } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import type { CreditPackConfig } from '@/types/credit-packs';
import { formatCreditPrice, getPricePerCredit } from '@/types/credit-packs';

interface CreditPackCardProps {
  pack: CreditPackConfig;
  onSelect: (pack: CreditPackConfig) => void;
  isLoading?: boolean;
  disabled?: boolean;
}

export function CreditPackCard({
  pack,
  onSelect,
  isLoading = false,
  disabled = false,
}: CreditPackCardProps) {
  const handleSelect = () => {
    if (!disabled) {
      onSelect(pack);
    }
  };

  return (
    <Card
      data-testid={`credit-pack-${pack.sku}`}
      className={cn(
        'relative flex flex-col transition-shadow hover:shadow-md',
        pack.popular && 'border-primary shadow-md'
      )}
    >
      {pack.popular && (
        <Badge
          className="absolute -top-2.5 left-1/2 -translate-x-1/2"
          data-testid="popular-badge"
        >
          Best Value
        </Badge>
      )}

      {pack.savings && (
        <Badge
          variant="secondary"
          className="absolute -top-2.5 right-2 bg-green-500 text-white"
          data-testid="savings-badge"
        >
          {pack.savings}
        </Badge>
      )}

      <CardHeader className="text-center pb-2">
        <div className="mx-auto mb-2 flex h-12 w-12 items-center justify-center rounded-full bg-primary/10">
          <Coins className="h-6 w-6 text-primary" />
        </div>
        <CardTitle className="text-xl">{pack.name}</CardTitle>
      </CardHeader>

      <CardContent className="flex-1 space-y-2 text-center">
        <div>
          <span className="text-3xl font-bold">{formatCreditPrice(pack.priceInCents)}</span>
        </div>
        <p className="text-sm text-muted-foreground">
          {getPricePerCredit(pack)} per credit
        </p>
      </CardContent>

      <CardFooter>
        <Button
          data-testid={`buy-credits-${pack.sku}`}
          className="w-full"
          variant={pack.popular ? 'default' : 'outline'}
          onClick={handleSelect}
          disabled={isLoading || disabled}
        >
          {isLoading ? 'Processing...' : 'Buy Now'}
        </Button>
      </CardFooter>
    </Card>
  );
}
